/**
 * The panel that takes the composer's place once the free question is spent.
 *
 * Three rules shaped this, and each of them rules out something obvious:
 *
 * · IT IS NOT A MODAL. A sheet over the thread would hide the answer, and the
 *   answer is the entire argument for making an account. So the gate sits
 *   exactly where the composer sat and covers nothing.
 *
 * · THE COMPOSER IS REMOVED, NOT DISABLED. A greyed-out field with a cursor
 *   that will not land invites people to type into nothing and reads as a bug.
 *   The host renders one or the other, never a dead one.
 *
 * · THE ANSWER IS NEVER TOUCHED. Nothing here blurs, truncates, fades or
 *   retracts a turn that was already shown. Whatever they read for free stays
 *   readable, refresh included.
 *
 * Email then code, in place: the same passwordless flow as everywhere else
 * (`authApi.sendSignInCode` / `verifySignInCode`), so a new address creates the
 * account and a known one signs in. The traveller is never asked to choose
 * between signing up and signing in, and the copy never asks them to.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { authApi } from "../../auth/api/auth.api";
import { panelStyle } from "../../../shared/ui/card-frame";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

/* ---------- Skin ---------- */

const NAVY = "#110E39";
const GOLD = "#E5B265";
const MUTED = "#5a5d77";
/** Rest state: one navy hairline. Never a gradient. */
const HAIRLINE = "rgba(17,14,57,0.22)";
const RED = "#b3123c";

const RESEND_COOLDOWN_S = 30;

type Step = "intro" | "email" | "code";

export interface ConciergeSignInGateProps {
  /** Fired once the code is accepted and the session exists. */
  onSignedIn: () => void;
  /**
   * Tighter type and padding for the Discover side panel, which is roughly
   * half the width of the modal and the standalone page.
   */
  compact?: boolean;
}

export function ConciergeSignInGate({
  onSignedIn,
  compact = false,
}: ConciergeSignInGateProps) {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>("intro");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearInterval(id);
  }, [resendIn > 0]);

  useEffect(() => {
    if (step === "email") emailRef.current?.focus();
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  const emailValid = /\S+@\S+\.\S+/.test(email.trim());

  async function sendCode(): Promise<boolean> {
    if (!emailValid) {
      setError(t("ai_concierge.gate.invalid_email", {
        defaultValue: "Enter a valid email address.",
      }));
      emailRef.current?.focus();
      return false;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await authApi.sendSignInCode({ email: email.trim() });
      if (result?.error) {
        setError(
          result.error.status === 429
            ? t("ai_concierge.gate.too_many", {
                defaultValue:
                  "Too many codes requested. Try again in a few minutes.",
              })
            : (result.error.message ??
              t("ai_concierge.gate.send_failed", {
                defaultValue: "We could not send the code. Try again.",
              })),
        );
        return false;
      }
      setResendIn(RESEND_COOLDOWN_S);
      return true;
    } catch {
      setError(t("ai_concierge.gate.send_failed", {
        defaultValue: "We could not send the code. Try again.",
      }));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    if (!/^\d{6}$/.test(value)) {
      setError(t("ai_concierge.gate.code_incomplete", {
        defaultValue: "Enter the six digit code.",
      }));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await authApi.verifySignInCode({
        email: email.trim(),
        code: value,
      });
      if (result?.error) {
        setError(
          result.error.message ??
            t("ai_concierge.gate.code_invalid", {
              defaultValue: "That code is wrong or has expired.",
            }),
        );
        setCode("");
        codeRef.current?.focus();
        return;
      }
      onSignedIn();
    } catch {
      setError(t("ai_concierge.gate.code_invalid", {
        defaultValue: "That code is wrong or has expired.",
      }));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  function onCodeChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (error) setError(null);
    if (digits.length === 6 && !busy) void verify(digits);
  }

  const pad = compact ? "12px 14px 14px" : "16px 18px 18px";

  return (
    <div
      className="mj-gate"
      role="region"
      aria-label={t("ai_concierge.gate.region_label", {
        defaultValue: "Make an account to keep planning",
      })}
      style={{ ...panelStyle(compact ? 16 : 20), padding: pad }}
    >
      <style>{GATE_CSS}</style>

      {step === "intro" ? (
        // C15: the chat is kept and picks up where it stopped, so the panel
        // says so, and one button opens the email step (sign in and sign up
        // are the same passwordless flow, so there is no second choice).
        <div className="mj-gate-step">
          <p
            className="text-start"
            style={{
              margin: 0,
              fontSize: compact ? 14 : 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: NAVY,
            }}
          >
            {t("ai_concierge.gate.keep_going_title", {
              defaultValue: "Sign in to keep going",
            })}
          </p>
          <p
            className="text-start"
            style={{
              margin: "4px 0 0",
              fontSize: compact ? 12.5 : 13,
              lineHeight: 1.5,
              color: MUTED,
            }}
          >
            {t("ai_concierge.gate.keep_going_body", {
              defaultValue: "Your chat is saved, and picks up right here after you sign in.",
            })}
          </p>

          <PrimaryButton compact={compact} onClick={() => setStep("email")}>
            {t("ai_concierge.gate.keep_going_cta", {
              defaultValue: "Sign in, it takes 10 seconds",
            })}
          </PrimaryButton>
        </div>
      ) : step === "email" ? (
        <form
          className="mj-gate-step"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await sendCode()) {
              setCode("");
              setStep("code");
            }
          }}
        >
          <Label htmlFor="mj-gate-email">
            {t("ai_concierge.gate.email_label", { defaultValue: "Email" })}
          </Label>
          <Field
            id="mj-gate-email"
            ref={emailRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            dir="ltr"
            value={email}
            invalid={!!error}
            describedBy={error ? "mj-gate-error" : undefined}
            placeholder={t("ai_concierge.gate.email_placeholder", {
              defaultValue: "you@example.com",
            })}
            onChange={(v) => {
              setEmail(v);
              if (error) setError(null);
            }}
          />
          {error && <InlineError id="mj-gate-error">{error}</InlineError>}

          <PrimaryButton compact={compact} type="submit" disabled={busy}>
            {busy
              ? t("ai_concierge.gate.sending", { defaultValue: "Sending…" })
              : t("ai_concierge.gate.send_code", { defaultValue: "Send code" })}
          </PrimaryButton>

          <Footnote />
        </form>
      ) : (
        <form
          className="mj-gate-step"
          onSubmit={(e) => {
            e.preventDefault();
            void verify(code);
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: compact ? 12.5 : 13.5,
              lineHeight: 1.55,
              color: MUTED,
            }}
          >
            {t("ai_concierge.gate.code_sub", {
              defaultValue: "We sent a six digit code to {{email}}",
              email: email.trim(),
            })}{" "}
            <QuietButton
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
            >
              {t("ai_concierge.gate.wrong_email", {
                defaultValue: "Wrong email?",
              })}
            </QuietButton>
          </p>

          <div style={{ marginTop: 10 }}>
            <Field
              id="mj-gate-code"
              ref={codeRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={6}
              centered
              value={code}
              invalid={!!error}
              describedBy={error ? "mj-gate-error" : undefined}
              ariaLabel={t("ai_concierge.gate.code_label", {
                defaultValue: "Six digit code",
              })}
              placeholder="000000"
              onChange={onCodeChange}
            />
          </div>
          {error && <InlineError id="mj-gate-error">{error}</InlineError>}

          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              fontSize: 12.5,
              color: MUTED,
            }}
          >
            {resendIn > 0 ? (
              <span>
                {t("ai_concierge.gate.resend_in", {
                  defaultValue: "Send it again in {{seconds}}s",
                  seconds: resendIn,
                })}
              </span>
            ) : (
              <QuietButton disabled={busy} onClick={() => void sendCode()}>
                {t("ai_concierge.gate.resend", {
                  defaultValue: "Send it again",
                })}
              </QuietButton>
            )}
          </div>

          <PrimaryButton
            compact={compact}
            type="submit"
            disabled={busy || code.length !== 6}
          >
            {busy
              ? t("ai_concierge.gate.verifying", { defaultValue: "Checking…" })
              : t("ai_concierge.gate.verify", { defaultValue: "Continue" })}
          </PrimaryButton>

          <Footnote />
        </form>
      )}
    </div>
  );
}

/* ==================================================================== */
/*  Primitives                                                          */
/* ==================================================================== */

function Label({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-start"
      style={{
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: MUTED,
      }}
    >
      {children}
    </label>
  );
}

/**
 * White, one navy hairline at rest, navy when focused, red on error.
 *
 * The ring is a box-shadow rather than a border so focus does not move the
 * text by a pixel, and it is a flat colour rather than a gradient: the frame
 * band belongs to the panel, never to a control inside it.
 */
function Field({
  id,
  ref,
  value,
  onChange,
  invalid,
  describedBy,
  ariaLabel,
  centered = false,
  ...rest
}: {
  id: string;
  ref: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  describedBy?: string;
  ariaLabel?: string;
  centered?: boolean;
  type: string;
  inputMode?: "email" | "numeric";
  autoComplete?: string;
  dir?: string;
  maxLength?: number;
  placeholder?: string;
}) {
  const [focused, setFocused] = useState(false);
  const ring = invalid
    ? `0 0 0 1px ${RED}`
    : focused
      ? `0 0 0 1px ${NAVY}, 0 0 0 3px rgba(17,14,57,0.10)`
      : `0 0 0 1px ${HAIRLINE}`;

  return (
    <input
      {...rest}
      id={id}
      ref={ref}
      value={value}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className="mj-gate-field w-full rounded-[13px] bg-white outline-none"
      style={{
        minHeight: 44,
        padding: "10px 12px",
        fontSize: centered ? 18 : 14.5,
        fontWeight: centered ? 700 : 400,
        letterSpacing: centered ? "0.32em" : undefined,
        textAlign: centered ? "center" : "start",
        fontVariantNumeric: centered ? "tabular-nums" : undefined,
        color: NAVY,
        boxShadow: ring,
      }}
    />
  );
}

function InlineError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p
      id={id}
      role="alert"
      className="text-start"
      style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.45, color: RED }}
    >
      {children}
    </p>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  compact,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  compact?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="mj-gate-primary mj-press w-full rounded-full"
      style={{
        marginTop: 12,
        minHeight: compact ? 40 : 44,
        padding: "10px 16px",
        fontSize: compact ? 13.5 : 14.5,
        fontWeight: 700,
        color: "#fff",
        background: BEVEL_NAVY_FACE,
        boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

/** The quieter option. Text, navy, underlined, with a 24px hit area. */
function QuietButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mj-gate-quiet font-semibold underline underline-offset-2"
      style={{
        minHeight: 24,
        padding: "4px 2px",
        fontSize: 12.5,
        color: NAVY,
        background: "none",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
      }}
    >
      {children}
    </button>
  );
}

function Footnote() {
  const { t } = useTranslation();
  return (
    <p
      style={{
        margin: "10px 0 0",
        textAlign: "center",
        fontSize: 11.5,
        color: MUTED,
      }}
    >
      <span aria-hidden style={{ color: GOLD }}>{"✦"}</span>{" "}
      {t("ai_concierge.gate.passwordless", {
        defaultValue: "No password, ever. Just the code.",
      })}
    </p>
  );
}

/**
 * Transform and opacity only, 200ms, ease-out. Nothing here animates height or
 * colour, so a step change cannot reflow the thread above it.
 */
const GATE_CSS = `
@keyframes mj-gate-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}
.mj-gate-step { animation: mj-gate-in 200ms cubic-bezier(0.22, 1, 0.36, 1) backwards; }
.mj-gate-primary, .mj-gate-quiet { transition: opacity 160ms cubic-bezier(0.22, 1, 0.36, 1); }
.mj-gate-primary:hover:not(:disabled) { opacity: 0.88; }
.mj-gate-quiet:hover:not(:disabled) { opacity: 0.7; }
.mj-gate-field::placeholder { color: #9a9bb5; letter-spacing: normal; }
@media (prefers-reduced-motion: reduce) {
  .mj-gate-step { animation: none; }
  .mj-gate-primary, .mj-gate-quiet { transition: none; }
}
`;
